
import Vue from "vue";
import modal from "@/components/common/Modal.vue";
import LabsService from "@/services/LabsService";
import LocationsService from "@/services/LocationsService";

type BulkUpdateResult = {
  successCount: number;
  failedIds: string[];
  failureDetails: { id: string; reason: string }[];
};

type FailureDetail = BulkUpdateResult["failureDetails"][number];

const parseIds = (value: string) =>
  Array.from(
    new Set(
      value
        .split(/[\s,]+/)
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );

const buildBooleanMap = (ids: string[], value: boolean) =>
  ids.reduce((acc, id) => {
    acc[id] = value;
    return acc;
  }, {} as Record<string, boolean>);

export default Vue.extend({
  components: { modal },
  props: {
    feature: { type: Object, required: true },
  },
  data() {
    return {
      enabled: true,
      locationTargetType: "locations",
      idsInput: "",
      saving: false,
      validationError: "",
      resultMessage: "",
      resultFailedIds: [] as string[],
      resultFailureDetails: [] as FailureDetail[],
    };
  },
  computed: {
    parsedIds(): string[] {
      return parseIds(this.idsInput);
    },
    idsLabel(): string {
      if (this.feature.type === "company") return "Company IDs";
      return this.locationTargetType === "companies"
        ? "Company IDs"
        : "Location IDs";
    },
    idsPlaceholder(): string {
      if (this.feature.type === "company") {
        return "Enter company IDs separated by commas, spaces, or new lines";
      }

      return this.locationTargetType === "companies"
        ? "Enter company IDs separated by commas, spaces, or new lines"
        : "Enter location IDs separated by commas, spaces, or new lines";
    },
  },
  methods: {
    validate() {
      return this.parsedIds.length ? "" : "At least one ID is required.";
    },
    getFailureReason(error: any) {
      return (
        error?.response?.data?.message ||
        error?.message ||
        "Unknown error"
      );
    },
    buildResult(total: number, failureDetails: FailureDetail[]): BulkUpdateResult {
      const failedIds = failureDetails.map(({ id }) => id);
      return {
        successCount: total - failedIds.length,
        failedIds,
        failureDetails,
      };
    },
    collectFailures(results: PromiseSettledResult<unknown>[], ids: string[]): FailureDetail[] {
      return results.reduce((acc, result, index) => {
        if (result.status === "rejected") {
          acc.push({
            id: ids[index],
            reason: this.getFailureReason(result.reason),
          });
        }
        return acc;
      }, [] as FailureDetail[]);
    },
    async runCompanyUpdates(
      updater: (companyId: string) => Promise<unknown>
    ): Promise<BulkUpdateResult> {
      const results = await Promise.allSettled(
        this.parsedIds.map((companyId) => updater(companyId))
      );
      return this.buildResult(
        this.parsedIds.length,
        this.collectFailures(results, this.parsedIds)
      );
    },
    async updateHiddenCompanyFeatures(): Promise<BulkUpdateResult> {
      return this.runCompanyUpdates((companyId) =>
        LabsService.updateFeatureFlag({
          companyId,
          featureKey: this.feature.key,
          enabled: this.enabled,
        })
      );
    },
    async updateHiddenLocationFeaturesByLocations(): Promise<BulkUpdateResult> {
      const locationResults = await Promise.allSettled(
        this.parsedIds.map(async (locationId) => {
          const response = await LocationsService.getLocationById(locationId);
          const companyId = response?.data?.location?.companyId;
          if (!companyId) {
            throw new Error(`Missing companyId for location ${locationId}`);
          }
          return { locationId, companyId };
        })
      );

      const failureDetails = this.collectFailures(locationResults, this.parsedIds);
      const companyLocationMap = locationResults.reduce((acc, result) => {
        if (result.status === "fulfilled") {
          const { companyId, locationId } = result.value;
          (acc[companyId] ||= []).push(locationId);
        }
        return acc;
      }, {} as Record<string, string[]>);

      const companyIds = Object.keys(companyLocationMap);
      const updateResults = await Promise.allSettled(
        companyIds.map((companyId) =>
          LabsService.updateFeatureFlagConfig({
            companyId,
            featureKey: this.feature.key,
            locationEnabled: buildBooleanMap(
              companyLocationMap[companyId],
              this.enabled
            ),
            locationVisibility: buildBooleanMap(
              companyLocationMap[companyId],
              true
            ),
          })
        )
      );

      this.collectFailures(updateResults, companyIds).forEach(({ id, reason }) => {
        companyLocationMap[id].forEach((locationId) => {
          failureDetails.push({ id: locationId, reason });
        });
      });

      return this.buildResult(this.parsedIds.length, failureDetails);
    },
    async updateHiddenLocationFeaturesByCompanies(): Promise<BulkUpdateResult> {
      return this.runCompanyUpdates((companyId) =>
        LabsService.updateFeatureFlagConfig({
          companyId,
          featureKey: this.feature.key,
          companyEnabled: this.enabled,
          companyVisibility: true,
        })
      );
    },
    getUpdater() {
      if (this.feature.type === "company") {
        return this.updateHiddenCompanyFeatures;
      }
      return this.locationTargetType === "companies"
        ? this.updateHiddenLocationFeaturesByCompanies
        : this.updateHiddenLocationFeaturesByLocations;
    },
    async submit() {
      this.validationError = this.validate();
      if (this.validationError) return;

      this.saving = true;
      this.resultMessage = "";
      this.resultFailedIds = [];
      this.resultFailureDetails = [];

      try {
        const result = await this.getUpdater().call(this);
        const action = this.enabled ? "Enabled" : "Disabled";

        this.resultMessage = result.failedIds.length
          ? `${action} ${result.successCount}. ${result.failedIds.length} failed.`
          : `${action} ${result.successCount}.`;
        this.resultFailedIds = result.failedIds;
        this.resultFailureDetails = result.failureDetails;
      } catch (error: any) {
        this.validationError =
          error?.response?.data?.message ||
          "Unable to update the hidden feature.";
      } finally {
        this.saving = false;
      }
    },
  },
});
